import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Radio,
  FormControlLabel,
  Switch,
  Collapse,
  Link,
  Tooltip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { isWhatsAppOfficialChannelTipo } from "../constants/channelCatalog";
import {
  assessSurveyWhatsappTemplate,
  surveyTemplateIssueCopy,
} from "../utils/surveyWhatsappTemplateCompat";

const SAMPLE_TEMPLATE_BODY_3 = `Olá {{1}}! Como foi o atendimento da solicitação {{2}}?

Responda nossa pesquisa em até 2 minutos:
{{3}}

Sua opinião nos ajuda a melhorar.`;

const SAMPLE_TEMPLATE_BODY_1 = `Obrigado pelo contato! Avalie nosso atendimento:
{{1}}`;

function surveyLabel(s) {
  if (!s) return "";
  const title = s.title || s.titulo || s.nome || `#${s.id}`;
  const metric = s.metricType || s.metric_type;
  return metric ? `${title} (${String(metric).toUpperCase()})` : title;
}

function approvedTemplates(list) {
  return (Array.isArray(list) ? list : []).filter((tpl) =>
    ["approved", "active", "enabled"].includes(
      String(tpl.status || "").trim().toLowerCase()
    )
  );
}

function TemplateCompatFeedback({ template, t }) {
  if (!template) return null;
  const assessment = assessSurveyWhatsappTemplate(template);
  const copy = surveyTemplateIssueCopy(t, assessment);
  if (!copy) return null;
  return (
    <Alert severity={copy.severity} sx={{ mt: 0.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {copy.title}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.75 }}>
        <strong>{t("channelForm.closingSurveys.templateIssues.causeLabel")}</strong>{" "}
        {copy.cause}
      </Typography>
      <Typography variant="body2">
        <strong>{t("channelForm.closingSurveys.templateIssues.fixLabel")}</strong>{" "}
        {copy.fix}
      </Typography>
    </Alert>
  );
}

function findTemplateById(templates, templateId) {
  const id = Number(templateId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return (templates || []).find((tpl) => Number(tpl.id) === id) || null;
}

function MetaTemplateGuide({ open, onToggle, t, copiedKey, onCopy }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={onToggle}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        {t("channelForm.closingSurveys.metaGuideTitle")}
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Link>

      <Collapse in={open}>
        <Stack spacing={1.25} sx={{ mt: 1.25, pl: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t("channelForm.closingSurveys.metaGuideIntro")}
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, pl: 2.25, color: "text.secondary", typography: "body2" }}
          >
            <li>{t("channelForm.closingSurveys.metaGuideBullet1")}</li>
            <li>{t("channelForm.closingSurveys.metaGuideBullet2")}</li>
            <li>{t("channelForm.closingSurveys.metaGuideBullet3")}</li>
            <li>{t("channelForm.closingSurveys.metaGuideBullet4")}</li>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("channelForm.closingSurveys.paramLegend")}
          </Typography>
          {[
            {
              key: "3",
              title: t("channelForm.closingSurveys.sample3Title"),
              body: SAMPLE_TEMPLATE_BODY_3,
            },
            {
              key: "1",
              title: t("channelForm.closingSurveys.sample1Title"),
              body: SAMPLE_TEMPLATE_BODY_1,
            },
          ].map((sample) => (
            <Box
              key={sample.key}
              sx={(theme) => ({
                p: 1.25,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: theme.palette.divider,
              })}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.75 }}
              >
                <Typography variant="caption" fontWeight={600}>
                  {sample.title}
                </Typography>
                <Tooltip
                  title={
                    copiedKey === sample.key
                      ? t("channelForm.closingSurveys.copied")
                      : t("channelForm.closingSurveys.copySample")
                  }
                >
                  <IconButton size="small" onClick={() => onCopy(sample.key, sample.body)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: "pre-wrap",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: "text.secondary",
                }}
              >
                {sample.body}
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            {t("channelForm.closingSurveys.metaGuideWarning")}
          </Typography>
        </Stack>
      </Collapse>
    </Box>
  );
}

/**
 * Disclosure progressivo:
 * 1) switch “enviar pesquisa”
 * 2) escolher a pesquisa padrão
 * 3) (WA) template / guia Meta sob demanda
 * 4) adicionar mais pesquisas só depois da primeira
 */
export default function ChannelClosingSurveysSection({
  items = [],
  onChangeItems,
  publishedSurveys = [],
  loadingSurveys = false,
  channelTemplates = [],
  channelTemplatesLoading = false,
  channelTipo,
  channelSaved = false,
}) {
  const { t } = useTranslation("settings");
  const isOfficialWa = isWhatsAppOfficialChannelTipo(channelTipo);

  const enabled = items.length > 0;
  const showAdvancedList = items.length > 1;

  const [waOptionsOpen, setWaOptionsOpen] = useState(() =>
    Boolean(isOfficialWa && items.some((r) => r.templateId))
  );
  const [guideOpen, setGuideOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [moreOpen, setMoreOpen] = useState(() => items.length > 1);

  useEffect(() => {
    if (items.some((r) => r.templateId)) setWaOptionsOpen(true);
  }, [items]);

  useEffect(() => {
    if (items.length > 1) setMoreOpen(true);
  }, [items.length]);

  const templates = useMemo(
    () => approvedTemplates(channelTemplates),
    [channelTemplates]
  );

  const usedSurveyIds = useMemo(
    () => new Set(items.map((r) => Number(r.surveyId)).filter(Boolean)),
    [items]
  );

  const availableSurveys = useMemo(
    () => (publishedSurveys || []).filter((s) => !usedSurveyIds.has(Number(s.id))),
    [publishedSurveys, usedSurveyIds]
  );

  const defaultIndex = Math.max(
    0,
    items.findIndex((r) => r.isDefault)
  );
  const defaultRow = items[defaultIndex] || null;

  const updateRow = (index, patch) => {
    onChangeItems(items.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const setDefault = (index) => {
    onChangeItems(items.map((row, i) => ({ ...row, isDefault: i === index })));
  };

  const removeRow = (index) => {
    const next = items.filter((_, i) => i !== index);
    if (next.length && !next.some((r) => r.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    onChangeItems(next);
  };

  const addRow = () => {
    const first = availableSurveys[0];
    if (!first) return;
    onChangeItems([
      ...items,
      {
        surveyId: Number(first.id),
        templateId: null,
        isDefault: items.length === 0,
      },
    ]);
    setMoreOpen(true);
  };

  const enableWithFirstSurvey = () => {
    const first = (publishedSurveys || [])[0];
    if (!first) return;
    onChangeItems([
      {
        surveyId: Number(first.id),
        templateId: null,
        isDefault: true,
      },
    ]);
  };

  const disableAll = () => onChangeItems([]);

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch {
      /* ignore */
    }
  };

  const surveyOptionsFor = (row) =>
    (publishedSurveys || []).filter(
      (s) => Number(s.id) === Number(row.surveyId) || !usedSurveyIds.has(Number(s.id))
    );

  return (
    <Box sx={{ width: "100%" }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={loadingSurveys || (!enabled && publishedSurveys.length === 0)}
            onChange={(e) => {
              if (e.target.checked) enableWithFirstSurvey();
              else disableAll();
            }}
          />
        }
        label={t("channelForm.closingSurveys.switchLabel")}
      />
      {Boolean(t("channelForm.closingSurveys.switchHelper")) && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: -0.5, mb: enabled ? 1.5 : 0 }}
        >
          {t("channelForm.closingSurveys.switchHelper")}
        </Typography>
      )}

      {!enabled && publishedSurveys.length === 0 && !loadingSurveys && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {t("channelForm.closingSurveys.noPublishedSurveys")}
        </Typography>
      )}

      <Collapse in={enabled}>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {/* Passo 2: pesquisa padrão */}
          {defaultRow && (
            <TextField
              select
              fullWidth
              size="small"
              label={t("channelForm.closingSurveys.surveyLabel")}
              value={defaultRow.surveyId || ""}
              disabled={loadingSurveys}
              onChange={(e) =>
                updateRow(defaultIndex, {
                  surveyId: Number(e.target.value) || null,
                })
              }
              helperText={t("channelForm.closingSurveys.defaultSurveyHelper")}
            >
              {loadingSurveys && (
                <MenuItem value={defaultRow.surveyId || ""} disabled>
                  {t("channelForm.common.loadingSurveys")}
                </MenuItem>
              )}
              {surveyOptionsFor(defaultRow).map((s) => (
                <MenuItem key={s.id} value={Number(s.id)}>
                  {surveyLabel(s)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Passo 3 (WA): template — só sob demanda */}
          {isOfficialWa && defaultRow && (
            <Box>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => setWaOptionsOpen((v) => !v)}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {t("channelForm.closingSurveys.waOptionsLink")}
                {waOptionsOpen ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </Link>

              <Collapse in={waOptionsOpen}>
                <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                  {(() => {
                    const selectedTpl = findTemplateById(
                      templates,
                      defaultRow.templateId
                    );
                    const assessment = selectedTpl
                      ? assessSurveyWhatsappTemplate(selectedTpl)
                      : { ok: true };
                    const hasError = Boolean(
                      defaultRow.templateId && assessment.ok === false
                    );
                    return (
                      <>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label={t("channelForm.closingSurveys.templateLabel")}
                          value={defaultRow.templateId || ""}
                          disabled={!channelSaved || channelTemplatesLoading}
                          error={hasError}
                          onChange={(e) =>
                            updateRow(defaultIndex, {
                              templateId:
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                            })
                          }
                          helperText={
                            !channelSaved
                              ? t(
                                  "channelForm.common.surveyWhatsappTemplateSaveFirst"
                                )
                              : hasError
                                ? " "
                                : t("channelForm.closingSurveys.templateHelper")
                          }
                        >
                          <MenuItem value="">
                            {t("channelForm.closingSurveys.templateNone")}
                          </MenuItem>
                          {channelTemplatesLoading && (
                            <MenuItem
                              value={defaultRow.templateId || ""}
                              disabled
                            >
                              {t("channelForm.common.loadingTemplates")}
                            </MenuItem>
                          )}
                          {templates.map((tpl) => {
                            const compat = assessSurveyWhatsappTemplate(tpl);
                            const label = `${
                              tpl.elementName || tpl.nome || tpl.name || `#${tpl.id}`
                            }${tpl.languageCode ? ` · ${tpl.languageCode}` : ""}`;
                            return (
                              <MenuItem key={tpl.id} value={Number(tpl.id)}>
                                {compat.ok
                                  ? label
                                  : t(
                                      "channelForm.closingSurveys.templateIncompatibleOption",
                                      { name: label }
                                    )}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                        <TemplateCompatFeedback
                          template={selectedTpl}
                          t={t}
                        />
                      </>
                    );
                  })()}

                  <MetaTemplateGuide
                    open={guideOpen}
                    onToggle={() => setGuideOpen((v) => !v)}
                    t={t}
                    copiedKey={copiedKey}
                    onCopy={copyText}
                  />
                </Stack>
              </Collapse>
            </Box>
          )}

          {/* Passo 4: mais pesquisas — só depois da primeira */}
          {defaultRow && (
            <Box>
              {!showAdvancedList ? (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                  disabled={loadingSurveys || availableSurveys.length === 0}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  {t("channelForm.closingSurveys.addAnotherSurvey")}
                </Button>
              ) : (
                <>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => setMoreOpen((v) => !v)}
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
                  >
                    {t("channelForm.closingSurveys.moreSurveysLink", {
                      count: items.length,
                    })}
                    {moreOpen ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </Link>

                  <Collapse in={moreOpen}>
                    <Stack spacing={1.25}>
                      <Typography variant="caption" color="text.secondary">
                        {t("channelForm.closingSurveys.moreSurveysHelper")}
                      </Typography>

                      {items.map((row, index) => {
                        if (index === defaultIndex) return null;
                        return (
                          <Stack
                            key={`${row.surveyId}-${index}`}
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ sm: "flex-start" }}
                          >
                            {items.length > 1 && (
                              <FormControlLabel
                                control={
                                  <Radio
                                    checked={Boolean(row.isDefault)}
                                    onChange={() => setDefault(index)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="caption" color="text.secondary">
                                    {t("channelForm.closingSurveys.defaultLabel")}
                                  </Typography>
                                }
                                sx={{ mr: 0, minWidth: 72 }}
                              />
                            )}

                            <TextField
                              select
                              size="small"
                              fullWidth
                              label={t("channelForm.closingSurveys.surveyLabel")}
                              value={row.surveyId || ""}
                              disabled={loadingSurveys}
                              onChange={(e) =>
                                updateRow(index, {
                                  surveyId: Number(e.target.value) || null,
                                })
                              }
                            >
                              {surveyOptionsFor(row).map((s) => (
                                <MenuItem key={s.id} value={Number(s.id)}>
                                  {surveyLabel(s)}
                                </MenuItem>
                              ))}
                            </TextField>

                            {isOfficialWa && (
                              <Box sx={{ width: "100%" }}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  label={t("channelForm.closingSurveys.templateLabel")}
                                  value={row.templateId || ""}
                                  disabled={!channelSaved || channelTemplatesLoading}
                                  error={Boolean(
                                    row.templateId &&
                                      findTemplateById(templates, row.templateId) &&
                                      !assessSurveyWhatsappTemplate(
                                        findTemplateById(templates, row.templateId)
                                      ).ok
                                  )}
                                  onChange={(e) =>
                                    updateRow(index, {
                                      templateId:
                                        e.target.value === ""
                                          ? null
                                          : Number(e.target.value),
                                    })
                                  }
                                >
                                  <MenuItem value="">
                                    {t("channelForm.closingSurveys.templateNone")}
                                  </MenuItem>
                                  {templates.map((tpl) => {
                                    const compat = assessSurveyWhatsappTemplate(tpl);
                                    const label =
                                      tpl.elementName ||
                                      tpl.nome ||
                                      tpl.name ||
                                      `#${tpl.id}`;
                                    return (
                                      <MenuItem key={tpl.id} value={Number(tpl.id)}>
                                        {compat.ok
                                          ? label
                                          : t(
                                              "channelForm.closingSurveys.templateIncompatibleOption",
                                              { name: label }
                                            )}
                                      </MenuItem>
                                    );
                                  })}
                                </TextField>
                                <TemplateCompatFeedback
                                  template={findTemplateById(templates, row.templateId)}
                                  t={t}
                                />
                              </Box>
                            )}

                            <IconButton
                              aria-label={t("channelForm.closingSurveys.remove")}
                              onClick={() => removeRow(index)}
                              size="small"
                              sx={{ mt: { sm: 0.25 } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        );
                      })}

                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addRow}
                        disabled={loadingSurveys || availableSurveys.length === 0}
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                      >
                        {t("channelForm.closingSurveys.addSurvey")}
                      </Button>
                    </Stack>
                  </Collapse>
                </>
              )}
            </Box>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

export function parseClosingSurveyItemsFromChannel(channel) {
  const raw = channel?.envia_pesquisas_json;
  let list = [];
  if (raw != null && raw !== "") {
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed)) {
        list = parsed
          .map((r) => ({
            surveyId: Number(r.surveyId || r.survey_id || r.id) || null,
            templateId: Number(r.templateId || r.template_id) || null,
            isDefault: Boolean(r.isDefault || r.is_default || r.default),
          }))
          .filter((r) => r.surveyId > 0);
      }
    } catch {
      list = [];
    }
  }
  if (!list.length) {
    const sid = Number(channel?.envia_pesquisa);
    if (Number.isFinite(sid) && sid > 0) {
      list = [
        {
          surveyId: sid,
          templateId: Number(channel?.envia_pesquisa_template) || null,
          isDefault: true,
        },
      ];
    }
  }
  if (list.length && !list.some((r) => r.isDefault)) {
    list[0] = { ...list[0], isDefault: true };
  }
  return list;
}

export function closingSurveyItemsToPayload(items) {
  const cleaned = (Array.isArray(items) ? items : [])
    .map((r) => ({
      surveyId: Number(r.surveyId) || null,
      templateId: Number(r.templateId) > 0 ? Number(r.templateId) : null,
      isDefault: Boolean(r.isDefault),
    }))
    .filter((r) => r.surveyId > 0);
  if (!cleaned.length) {
    return {
      envia_pesquisas_json: null,
      envia_pesquisa: 0,
      envia_pesquisa_template: null,
    };
  }
  let defIdx = cleaned.findIndex((r) => r.isDefault);
  if (defIdx < 0) defIdx = 0;
  const normalized = cleaned.map((r, i) => ({
    ...r,
    isDefault: i === defIdx,
  }));
  const def = normalized[defIdx];
  return {
    envia_pesquisas_json: normalized,
    envia_pesquisa: def.surveyId,
    envia_pesquisa_template: def.templateId || null,
  };
}
